import { useEffect, useState } from 'react'
import { CloudOff, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { flushOutbox, watchOutbox } from '@waago/api'

/**
 * Says when work is waiting to be sent.
 *
 * <p>The outbox exists so a lost signal does not lose what somebody typed. This
 * is the half that makes it trustworthy: queued work that is invisible is
 * indistinguishable from work that vanished, and a person who cannot tell will
 * do it again.
 *
 * <p>Shown only when there is something waiting. An "offline" banner that is up
 * whenever the browser thinks it is offline would be wrong most of the time —
 * `navigator.onLine` means "attached to a network", which a captive portal and
 * a dead cell both satisfy.
 */
export function OfflineBanner() {
  const { t } = useTranslation()
  const [count, setCount] = useState(0)
  const [flushing, setFlushing] = useState(false)

  useEffect(() => watchOutbox(setCount), [])

  if (count === 0) return null

  async function retry() {
    setFlushing(true)
    try {
      await flushOutbox()
    } finally {
      setFlushing(false)
    }
  }

  return (
    <div
      role="status"
      className="animate-slide-up flex items-center gap-2 bg-amber-50 px-4 py-2 text-xs text-amber-900"
    >
      <CloudOff className="size-4 shrink-0" aria-hidden="true" />
      <span className="flex-1">{t('common.queued', { count })}</span>
      <button
        type="button"
        onClick={() => void retry()}
        disabled={flushing}
        className="flex items-center gap-1 font-semibold underline underline-offset-2"
      >
        <RefreshCw className={flushing ? 'size-3.5 animate-spin' : 'size-3.5'} aria-hidden="true" />
        {t('common.retry')}
      </button>
    </div>
  )
}
